import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Switch } from '@/components/ui/switch';
import { Plus, Briefcase, Pencil, Trash2, Power, FolderPlus, ChevronRight, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useUser } from '@/utils/userHelper';

export default function ServiceManagement({ user: propUser, isTab, tabId }) {
  const user = useUser(propUser);
  const [services, setServices] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingService, setEditingService] = useState(null);
  
  // Form states
  const [groupFormData, setGroupFormData] = useState({ name: '', description: '', guest_orderable: false });
  const [serviceFormData, setServiceFormData] = useState({ name: '', price: '', group_id: '', guest_orderable: false, serve_time: '' });

  const canManage = ['super_admin', 'hotel_admin', 'manager'].includes(user?.role);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [servicesRes, groupsRes] = await Promise.all([
        api.get('/services'),
        api.get('/service-groups')
      ]);
      setServices(servicesRes.data);
      setGroups(groupsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Group functions
  const openAddGroupDialog = () => {
    setEditingGroup(null);
    setGroupFormData({ name: '', description: '', guest_orderable: false });
    setGroupDialogOpen(true);
  };

  const openEditGroupDialog = (group) => {
    setEditingGroup(group);
    setGroupFormData({ name: group.name, description: group.description || '', guest_orderable: group.guest_orderable || false });
    setGroupDialogOpen(true);
  };

  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGroup) {
        await api.put(`/service-groups/${editingGroup.id}`, groupFormData);
        toast.success('Group updated');
      } else {
        await api.post('/service-groups', groupFormData);
        toast.success('Group created');
      }
      setGroupDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(editingGroup ? 'Failed to update group' : 'Failed to create group');
    }
  };

  const toggleGroup = async (groupId) => {
    try {
      await api.patch(`/service-groups/${groupId}/toggle`);
      toast.success('Group status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteGroup = async (groupId, groupName) => {
    if (!window.confirm(`Are you sure you want to delete "${groupName}"?`)) return;
    try {
      await api.delete(`/service-groups/${groupId}`);
      toast.success('Group deleted');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete group'));
    }
  };

  // Service functions
  const openAddServiceDialog = (groupId = '') => {
    setEditingService(null);
    setServiceFormData({ name: '', price: '', group_id: groupId, guest_orderable: false, serve_time: '' });
    setServiceDialogOpen(true);
  };

  const openEditServiceDialog = (service) => {
    setEditingService(service);
    setServiceFormData({
      name: service.name,
      price: service.price.toString(),
      group_id: service.group_id || '',
      guest_orderable: service.guest_orderable || false,
      serve_time: service.serve_time?.toString() || ''
    });
    setServiceDialogOpen(true);
  };

  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: serviceFormData.name,
        price: parseFloat(serviceFormData.price),
        group_id: serviceFormData.group_id || null,
        guest_orderable: serviceFormData.guest_orderable,
        serve_time: serviceFormData.serve_time ? parseInt(serviceFormData.serve_time) : null
      };
      
      if (editingService) {
        await api.put(`/services/${editingService.id}`, data);
        toast.success('Service updated');
      } else {
        await api.post('/services', data);
        toast.success('Service created');
      }
      setServiceDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(editingService ? 'Failed to update service' : 'Failed to create service');
    }
  };

  const toggleService = async (serviceId) => {
    try {
      await api.patch(`/services/${serviceId}/toggle`);
      toast.success('Service status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteService = async (serviceId, serviceName) => {
    if (!window.confirm(`Are you sure you want to delete "${serviceName}"?`)) return;
    try {
      await api.delete(`/services/${serviceId}`);
      toast.success('Service deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  // Group services by group_id
  const getServicesForGroup = (groupId) => {
    return services.filter(s => s.group_id === groupId);
  };

  const ungroupedServices = services.filter(s => !s.group_id);

  return (
    <div className="space-y-6" data-testid="service-management">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope' }}>Service Management</h1>
          <p className="text-muted-foreground mt-1">Manage service groups and services</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={openAddGroupDialog} data-testid="add-group-button">
              <FolderPlus className="h-4 w-4 mr-2" />
              Add Group
            </Button>
            <Button onClick={() => openAddServiceDialog()} data-testid="add-service-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Service
            </Button>
          </div>
        )}
      </div>

      {/* Group Dialog */}
      <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingGroup ? 'Edit Service Group' : 'Create Service Group'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleGroupSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="group-name">Group Name</Label>
              <Input
                id="group-name"
                placeholder="e.g., Laundry Services, Spa & Wellness"
                value={groupFormData.name}
                onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="group-description">Description (Optional)</Label>
              <Input
                id="group-description"
                placeholder="Brief description of this service category"
                value={groupFormData.description}
                onChange={(e) => setGroupFormData({ ...groupFormData, description: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-3 p-3 bg-violet-50 rounded-lg border border-violet-200">
              <Switch
                checked={groupFormData.guest_orderable}
                onCheckedChange={(checked) => setGroupFormData({ ...groupFormData, guest_orderable: checked })}
                data-testid="group-guest-orderable-switch"
              />
              <div>
                <Label className="text-violet-800 font-medium flex items-center gap-1">
                  <Users className="h-4 w-4" /> Guest Orderable
                </Label>
                <p className="text-xs text-violet-600">Services in this group will be shown in Guest Portal</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="flex-1">
                {editingGroup ? 'Update Group' : 'Create Group'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setGroupDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Service Dialog */}
      <Dialog open={serviceDialogOpen} onOpenChange={setServiceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingService ? 'Edit Service' : 'Add Service'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleServiceSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="service-group">Service Group</Label>
              <Select
                value={serviceFormData.group_id || "none"}
                onValueChange={(value) => setServiceFormData({ ...serviceFormData, group_id: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a group (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Group</SelectItem>
                  {groups.filter(g => g.is_active !== false).map(group => (
                    <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="service-name">Service Name</Label>
              <Input
                id="service-name"
                placeholder="e.g., Shirt Ironing, Deep Tissue Massage"
                value={serviceFormData.name}
                onChange={(e) => setServiceFormData({ ...serviceFormData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="service-price">Price (₹)</Label>
              <Input
                id="service-price"
                type="number"
                step="0.01"
                min="0"
                value={serviceFormData.price}
                onChange={(e) => setServiceFormData({ ...serviceFormData, price: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="service-serve-time">Serve Time (mins)</Label>
              <Input
                id="service-serve-time"
                type="number"
                min="0"
                value={serviceFormData.serve_time}
                onChange={(e) => setServiceFormData({ ...serviceFormData, serve_time: e.target.value })}
                placeholder="e.g., 30"
              />
              <p className="text-xs text-gray-500">Estimated time to deliver this service</p>
            </div>
            <div className="flex items-center gap-3 p-3 bg-violet-50 rounded-lg border border-violet-200">
              <Switch
                checked={serviceFormData.guest_orderable}
                onCheckedChange={(checked) => setServiceFormData({ ...serviceFormData, guest_orderable: checked })}
                data-testid="service-guest-orderable-switch"
              />
              <div>
                <Label className="text-violet-800 font-medium flex items-center gap-1">
                  <Users className="h-4 w-4" /> Guest Orderable
                </Label>
                <p className="text-xs text-violet-600">This service will be shown in Guest Portal</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="flex-1">
                {editingService ? 'Update Service' : 'Add Service'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setServiceDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="text-center py-12">Loading services...</div>
      ) : (
        <div className="space-y-4">
          {/* Service Groups with Accordion */}
          {groups.length > 0 && (
            <Accordion type="multiple" className="space-y-4">
              {groups.map(group => (
                <AccordionItem key={group.id} value={group.id} className="border rounded-lg">
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Briefcase className="h-5 w-5 text-primary" />
                        </div>
                        <div className="text-left">
                          <p className="font-semibold">{group.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {getServicesForGroup(group.id).length} services
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <Badge className={group.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {group.is_active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                        {canManage && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => toggleGroup(group.id)}>
                              <Power className={`h-4 w-4 ${group.is_active !== false ? 'text-green-600' : 'text-gray-400'}`} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => openEditGroupDialog(group)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteGroup(group.id, group.name)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    {group.description && (
                      <p className="text-sm text-muted-foreground mb-4">{group.description}</p>
                    )}
                    <div className="space-y-2">
                      {getServicesForGroup(group.id).map(service => (
                        <div
                          key={service.id}
                          className="flex items-center justify-between p-3 border rounded-lg bg-muted/50"
                        >
                          <div className="flex items-center gap-3">
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{service.name}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                              ₹{service.price}
                            </span>
                            <Badge className={service.is_available ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                              {service.is_available ? 'Active' : 'Inactive'}
                            </Badge>
                            {canManage && (
                              <div className="flex gap-1">
                                <Button size="sm" variant="ghost" onClick={() => toggleService(service.id)}>
                                  <Power className={`h-4 w-4 ${service.is_available ? 'text-green-600' : 'text-gray-400'}`} />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => openEditServiceDialog(service)}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteService(service.id, service.name)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      {getServicesForGroup(group.id).length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">No services in this group</p>
                      )}
                      {canManage && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full mt-2"
                          onClick={() => openAddServiceDialog(group.id)}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Service to {group.name}
                        </Button>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}

          {/* Ungrouped Services */}
          {ungroupedServices.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Other Services
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {ungroupedServices.map(service => (
                    <div
                      key={service.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{service.name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                          ₹{service.price}
                        </span>
                        <Badge className={service.is_available ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {service.is_available ? 'Active' : 'Inactive'}
                        </Badge>
                        {canManage && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => toggleService(service.id)}>
                              <Power className={`h-4 w-4 ${service.is_available ? 'text-green-600' : 'text-gray-400'}`} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => openEditServiceDialog(service)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteService(service.id, service.name)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Empty State */}
          {groups.length === 0 && services.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <Briefcase className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No service groups or services yet</p>
                {canManage && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Start by creating a service group, then add services to it
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
